/**
 * A surface's byte geometry, derived from its Godot `format` bitfield.
 * `vertex_data` is two concatenated regions, not one interleaved record: the
 * positions, then the normal/tangent frame. `attribute_data` is a third,
 * interleaved COLOR, UV1, UV2.
 */
/** Godot Mesh.ArrayFormat flags. */
export const ARRAY_FORMAT_VERTEX = 1 << 0;
export const ARRAY_FORMAT_NORMAL = 1 << 1;
export const ARRAY_FORMAT_TANGENT = 1 << 2;
const ARRAY_FORMAT_COLOR = 1 << 3;
const ARRAY_FORMAT_TEX_UV = 1 << 4;
const ARRAY_FORMAT_TEX_UV2 = 1 << 5;
/** Positions are 2×float32, not 3 (`rendering_server.h:339`). */
export const ARRAY_FLAG_USE_2D_VERTICES = 1 << 25;
/**
 * Attributes are quantised. `&` coerces to int32, so it reads only bits below 32, never
 * `ARRAY_FLAG_FORMAT_VERSION_2` (1 << 35): `surfaceVersion.ts` reads the version and upgrades an
 * older surface first.
 */
const ARRAY_FLAG_COMPRESS_ATTRIBUTES = 1 << 29;
/** RGBA8 vertex colour in both layouts, which Godot writes before UV1 in the attribute record. */
const COLOR_BYTES = 4;
/**
 * Godot `Mesh.PrimitiveType` (`servers/rendering_server.h`: POINTS 0, LINES 1, LINE_STRIP 2,
 * TRIANGLES 3, TRIANGLE_STRIP 4), the two this decoder reads. Any other read as triangles
 * fabricates faces.
 */
export const PRIMITIVE_POINTS = 0;
export const PRIMITIVE_TRIANGLES = 3;
export const PRIMITIVE_TRIANGLE_STRIP = 4;
export function surfaceLayout(format) {
    const compressed = (format & ARRAY_FLAG_COMPRESS_ATTRIBUTES) !== 0;
    const hasNormal = (format & ARRAY_FORMAT_NORMAL) !== 0;
    const hasTangent = (format & ARRAY_FORMAT_TANGENT) !== 0;
    const hasColor = (format & ARRAY_FORMAT_COLOR) !== 0;
    const hasUV = (format & ARRAY_FORMAT_TEX_UV) !== 0;
    const hasUV2 = (format & ARRAY_FORMAT_TEX_UV2) !== 0;
    // Compressed: 3×uint16 spanning the surface `aabb`, plus the tangent-frame
    // angle. Uncompressed: 3×float32.
    const positionStride = compressed ? 8 : 12;
    // Each octahedral pair is 2×uint16. Compressed folds the tangent into the
    // normal's 4 bytes as an axis-angle frame. Uncompressed gives each pair its own 4.
    const normalStride = hasNormal ? (compressed ? 4 : 4 + (hasTangent ? 4 : 0)) : 0;
    // 2×uint16 re-expanded by a non-zero `uv_scale` when compressed, else 2×float32.
    const uvBytes = compressed ? 4 : 8;
    return {
        compressed,
        tangent: hasNormal && hasTangent ? (compressed ? 'frame' : 'octahedral') : 'none',
        positionStride,
        normalStride,
        attributeStride: (hasColor ? COLOR_BYTES : 0) + (hasUV ? uvBytes : 0) + (hasUV2 ? uvBytes : 0),
        uvOffset: hasUV ? (hasColor ? COLOR_BYTES : 0) : -1,
        uv2Offset: hasUV2 ? (hasColor ? COLOR_BYTES : 0) + (hasUV ? uvBytes : 0) : -1,
    };
}
//# sourceMappingURL=surfaceFormat.js.map